import { toast } from 'sonner'
import type {
  BookingPhoneSaveResult,
  PrecontactPhoneResult,
  PrecontactSendResult,
  WaLinkReservationResult,
} from '@guilleumes/types'

/**
 * Què s'ensenya quan el backend ha **simulat** una escriptura a Beds24.
 *
 * Fora de producció el backend no escriu a Beds24 (`beds24-write-guard.util.ts`) i les respostes ho
 * diuen: `beds24Simulated: true` a les reserves, `simulated: true` al precontacte i als telèfons.
 * Viatgen amb els mateixos camps que un error (`beds24SyncError`, `sent: false`, `saved: false`)
 * perquè qui no coneix la marca no digui mai «enviat»; aquí es coneix, i es diu el que és: un
 * entorn de proves, no cap error.
 *
 * A producció la marca no hi és mai, i llavors cada funció torna exactament el toast de sempre.
 *
 * Aquí es tria el to i el text; `showResultToast` el pinta amb Sonner.
 */
export interface ResultToast {
  kind: 'success' | 'warning' | 'error' | 'info'
  title: string
  description?: string
}

/** `PUT /reservations/:id` simulat: el canvi és a la DB local i enlloc més. */
export const SIMULATED_UPDATE_TOAST: ResultToast = {
  kind: 'warning',
  title: 'Entorn de proves: reserva desada només en local',
  description: 'No ha sortit res cap a Beds24: allà la reserva segueix com estava.',
}

/** Cancel·lació simulada: cancel·lada al PMS local, viva a Beds24. */
export const SIMULATED_CANCEL_TOAST: ResultToast = {
  kind: 'warning',
  title: 'Entorn de proves: reserva cancel·lada només en local',
  description: 'No ha sortit res cap a Beds24: allà la reserva segueix activa.',
}

/** Precontacte simulat: l'hoste no ha rebut res i la fila segueix sent candidata. */
export const SIMULATED_SEND_TOAST: ResultToast = {
  kind: 'warning',
  title: "Entorn de proves: el missatge NO s'ha enviat",
  description: "No ha sortit res cap a Beds24 ni cap a l'hoste. La reserva es queda com estava.",
}

/** Telèfon simulat: el backend falla tancat, o sigui que tampoc no el desa en local. */
export const SIMULATED_PHONE_TOAST: ResultToast = {
  kind: 'warning',
  title: "Entorn de proves: el telèfon NO s'ha desat",
  description: "No ha sortit res cap a Beds24 i no s'ha desat res, tampoc en local.",
}

/** El resultat d'enviar un precontacte (`SendPrecontactDialog`). */
export function precontactSendToast(
  result: Pick<PrecontactSendResult, 'sent' | 'message' | 'simulated'>,
  guestName: string | null | undefined,
): ResultToast {
  if (result.simulated === true) return SIMULATED_SEND_TOAST

  if (result.sent) {
    return {
      kind: 'success',
      title: `Missatge enviat a ${guestName ?? "l'hoste"}`,
      description: result.message,
    }
  }

  // Un rebuig no és un error del sistema: el servidor ha tornat a mirar-ho i ha decidit que no
  // tocava. Cal que es llegeixi, no que passi desapercebut.
  return { kind: 'warning', title: "No s'ha enviat", description: result.message }
}

/**
 * El telèfon confirmat d'una resposta que **no** s'ha desat (`PrecontactSentTable`).
 * `null` = s'ha desat: el toast d'èxit, amb el botó de WhatsApp, el posa la taula.
 */
export function precontactPhoneNotSavedToast(
  result: Pick<PrecontactPhoneResult, 'saved' | 'message' | 'simulated'>,
): ResultToast | null {
  if (result.simulated === true) return SIMULATED_PHONE_TOAST
  if (result.saved) return null

  // El servidor ho ha tornat a mirar i ha dit que no. Cal que es llegeixi.
  return { kind: 'warning', title: "No s'ha desat", description: result.message }
}

/** El resultat de desar el telèfon copiat de l'extranet de Booking (`BookingPhoneForm`). */
export function bookingPhoneSaveToast(
  result: Pick<BookingPhoneSaveResult, 'saved' | 'phone' | 'message' | 'failed' | 'simulated'>,
  typedPhone: string,
): ResultToast {
  if (result.simulated === true) return SIMULATED_PHONE_TOAST

  if (result.saved && result.failed.length === 0) {
    return { kind: 'success', title: `Telèfon desat: ${result.phone ?? typedPhone}` }
  }

  // No s'ha desat, o en un grup Beds24 n'ha rebutjat alguna habitació: el servidor ho explica.
  return { kind: 'error', title: result.message }
}

/** La resposta d'una escriptura de reserva porta la marca `beds24Simulated: true`? */
export function isBeds24Simulated(data: unknown): boolean {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { beds24Simulated?: unknown }).beds24Simulated === true
  )
}

/**
 * El resultat d'un desat de reserva que pot haver escrit a Beds24: la fitxa (telèfon, client,
 * «Canviar dates, habitació o preu») i moure o estirar una estada al calendari
 * (`PATCH /reservations/:id` i `PATCH /reservations/:id/metadata`). Simulat → l'avís de
 * `SIMULATED_UPDATE_TOAST`; si no, l'èxit de sempre amb el text de cada lloc.
 */
export function reservationSaveToast(data: unknown, successTitle: string): ResultToast {
  if (isBeds24Simulated(data)) return SIMULATED_UPDATE_TOAST
  return { kind: 'success', title: successTitle }
}

/**
 * El telèfon de «Enllaça amb una reserva» de la safata de WhatsApp (`LinkReservationDialog`),
 * quan s'ha demanat desar-lo. L'enllaç ja s'ha fet i té el seu toast; això és només el telèfon.
 */
export function linkReservationPhoneToast(
  result: Pick<WaLinkReservationResult, 'phoneSaved' | 'phoneMessage' | 'beds24Simulated'>,
): ResultToast {
  if (result.beds24Simulated === true) return SIMULATED_PHONE_TOAST
  if (result.phoneSaved) return { kind: 'success', title: 'Telèfon desat a la reserva' }
  return { kind: 'warning', title: result.phoneMessage ?? "No s'ha pogut desar el telèfon" }
}

/** Pinta un `ResultToast` amb Sonner. Sense descripció, la crida no porta opcions. */
export function showResultToast(result: ResultToast): void {
  if (result.description) toast[result.kind](result.title, { description: result.description })
  else toast[result.kind](result.title)
}
